"use client";

import { Button } from "@/components/ui/button";
import { Smartphone, Download, User, Star, Apple } from "lucide-react";
import { SafeImage } from "@/components/ui/safe-image";
import { useState, useEffect } from "react";
import { Link } from "@/i18n/navigation";
import { useTranslations, useLocale } from "next-intl";
import { api, AppShowcase, Settings } from "@/lib/api";

export function DownloadAppSection() {
    const t = useTranslations("DownloadApp");
    const locale = useLocale();
    const [appData, setAppData] = useState<AppShowcase | null>(null);
    const [settings, setSettings] = useState<Settings | null>(null);
    const [currentIndex, setCurrentIndex] = useState(0);

    useEffect(() => {
        const fetchAppData = async () => {
            try {
                const [showcaseData, settingsData] = await Promise.all([
                    api.getAppShowcase(locale),
                    api.getSettings(locale)
                ]);
                setAppData(showcaseData);
                setSettings(settingsData);
            } catch (error) {
                console.error("Failed to fetch app showcase:", error);
            }
        };
        fetchAppData();
    }, [locale]);

    const localScreenshots = [
        "/mobile_app/resized/Screenshot_1776207351.png",
        "/mobile_app/resized/Screenshot_1776207399.png",
        "/mobile_app/resized/Screenshot_1776207414.png",
        "/mobile_app/resized/Screenshot_1776207418.png",
        "/mobile_app/resized/Screenshot_1776207423.png",
        "/mobile_app/resized/Screenshot_1776207429.png",
        "/mobile_app/resized/Screenshot_1776207448.png"
    ];

    const images = localScreenshots.length > 0 ? localScreenshots : (appData?.screenshots || []);

    const iosUrl = (appData?.ios_url && appData.ios_url !== "#" && !appData.ios_url.endsWith("/app")) 
        ? appData.ios_url 
        : (settings?.ios_url || "https://apps.apple.com/eg/app/almasria-medical-waste/id6770094618?l=ar");

    const androidUrl = (appData?.android_url && appData.android_url !== "#" && !appData.android_url.endsWith("/store/apps")) 
        ? appData.android_url 
        : (settings?.android_url || "https://play.google.com/store/apps/details?id=com.almasria.medical_waste_client");

    const clientAppLinks = {
        android: androidUrl,
        ios: iosUrl
    };

    const driverAppLinks = {
        android: androidUrl,
        ios: iosUrl
    };

    const handleDownload = (type: 'ios' | 'android', app: 'client' | 'driver') => {
        const links = app === 'client' ? clientAppLinks : driverAppLinks;
        const packageId = app === 'client' ? "com.almasria.medical_waste_client" : "com.medicalwaste.medical_waste";
        const scheme = "almasria://open";
        const storeUrl = type === 'ios' ? links.ios : links.android;

        if (typeof window === 'undefined') return;

        // On mobile, try deep linking
        const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
        
        if (isMobile) {
            const start = Date.now();
            
            if (/Android/i.test(navigator.userAgent)) {
                // Android Intent URL (very reliable)
                window.location.href = `intent://open#Intent;scheme=almasria;package=${packageId};S.browser_fallback_url=${encodeURIComponent(storeUrl)};end`;
            } else {
                // iOS Scheme URL
                window.location.href = scheme;
                setTimeout(() => {
                    if (Date.now() - start < 2500) {
                        window.location.href = storeUrl;
                    }
                }, 2000);
            }
        } else {
            // Desktop: just open store
            window.open(storeUrl, '_blank', 'noopener,noreferrer');
        }
    };

    useEffect(() => {
        if (images.length > 0) {
            const interval = setInterval(() => {
                setCurrentIndex((prev) => (prev + 1) % images.length);
            }, 4000);
            return () => clearInterval(interval);
        }
    }, [images.length]);

    return (
        <section id="download-app" className="py-24 md:py-32 relative overflow-hidden bg-slate-50 dark:bg-slate-900/30">
            {/* Background Decorations */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-[500px] bg-gradient-to-b from-primary/5 to-transparent pointer-events-none" />
            
            <div className="container px-4 md:px-6 relative z-10">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
                    
                    {/* Phone Mockup Showcase */}
                    <div 
                        className="relative flex items-center justify-center order-2 lg:order-1"
                    >
                         {/* Glowing abstract shape */}
                         <div className="absolute w-[450px] h-[450px] bg-secondary/10 rounded-full blur-[100px] animate-pulse" />
                         
                         {/* The Phone Container - Outer Frame */}
                         <div className="relative z-10 w-[320px] h-[554px] bg-slate-800 rounded-[3rem] border-[10px] border-slate-900 shadow-[0_50px_100px_-20px_rgba(0,0,0,0.5)] ring-1 ring-white/10">
                            
                            {/* Side Buttons for extra realism */}
                            <div className="absolute -left-[12px] top-24 w-[3px] h-12 bg-slate-700 rounded-l-md border-r border-white/5" />
                            <div className="absolute -left-[12px] top-40 w-[3px] h-12 bg-slate-700 rounded-l-md border-r border-white/5" />
                            <div className="absolute -right-[12px] top-32 w-[3px] h-16 bg-slate-700 rounded-r-md border-l border-white/5" />

                             {/* Inner Screen Container */}
                            <div className="absolute inset-x-0 inset-y-0 bg-slate-950 rounded-[2.2rem] overflow-hidden">
                                <div
                                    key={currentIndex}
                                    className="absolute inset-0 flex items-center justify-center bg-slate-950 animate-fade-in"
                                >
                                    <SafeImage
                                        src={images[currentIndex] || images[0]}
                                        alt={t("app_screenshot_alt")}
                                        fill
                                        sizes="320px"
                                        className="object-cover"
                                        unoptimized
                                    />
                                    {/* Screen Shine */}
                                    <div className="absolute inset-0 bg-gradient-to-tr from-white/10 via-transparent to-transparent pointer-events-none" />
                                </div>
                            </div>
                         </div>

                         {/* Side Decorations - Floating Icons */}
                         <div 
                            className="absolute -left-4 md:-left-8 lg:-left-10 top-20 rtl:left-auto rtl:-right-4 rtl:md:-right-10 bg-white/80 dark:bg-slate-800/80 backdrop-blur-xl p-4 rounded-3xl shadow-2xl z-20 flex flex-col items-center gap-1 border border-white/20"
                         >
                            <div className="flex text-amber-500">
                                {[1,2,3,4,5].map(i => <Star key={i} className="w-4 h-4 fill-current" />)}
                            </div>
                             <span className="text-xl font-black text-primary dark:text-white">{appData?.rating || "4.9"}</span>
                             <span className="text-[10px] uppercase font-black text-slate-400 tracking-tighter">{t("rating_label")}</span>
                          </div>

                         <div 
                            className="absolute -right-4 md:-right-8 lg:-right-10 bottom-32 rtl:right-auto rtl:-left-6 rtl:md:-left-12 bg-white/80 dark:bg-slate-800/80 backdrop-blur-xl p-5 rounded-[2rem] shadow-2xl z-20 flex gap-4 items-center border border-white/20"
                         >
                            <div className="w-12 h-12 bg-emerald-500 rounded-2xl flex items-center justify-center text-white shadow-lg shadow-emerald-500/30">
                                <Download className="w-6 h-6" />
                            </div>
                             <div>
                                 <p className="text-2xl font-black text-primary dark:text-white line-none tracking-tighter" dir="ltr">{appData?.download_counts || "50K+"}</p>
                                 <p className="text-[10px] text-slate-500 font-black uppercase tracking-tighter">{t("download_badge")}</p>
                             </div>
                          </div>
                    </div>
                    
                    {/* Content Section */}
                    <div className="space-y-12 order-1 lg:order-2">
                        <div className="space-y-6">
                            <div
                                className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/5 text-primary text-xs font-bold border border-primary/10 uppercase tracking-widest"
                            >
                                <Smartphone className="w-4 h-4" /> {t("badge")}
                            </div>
                            
                            <h2 
                                className="text-4xl md:text-7xl font-black font-heading tracking-tight text-primary dark:text-white leading-[1.1]"
                            >
                                {t("title_Line1")} <br/>
                                <span className="text-transparent bg-clip-text bg-gradient-to-r from-secondary to-amber-500">{t("title_Line2")}</span>
                            </h2>

                            <p 
                                className="text-lg md:text-xl text-slate-600 dark:text-slate-400 leading-relaxed max-w-xl"
                            >
                                {t("description")}
                            </p>
                        </div>

                        <div className="flex justify-center">
                            {/* Card 1: Patients/Clients */}
                            <div
                                className="glass-card p-8 rounded-[2.5rem] hover:border-primary/20 group relative overflow-hidden max-w-md w-full"
                            >
                                <div className="absolute top-0 right-0 w-24 h-24 bg-blue-500/5 blur-3xl rounded-full" />
                                <div className="p-4 bg-blue-500/10 rounded-2xl w-fit mb-6 group-hover:scale-110 group-hover:rotate-3 transition-all duration-500 shadow-lg shadow-blue-500/5">
                                    <User className="w-6 h-6 text-blue-500" />
                                </div>
                                <h3 className="text-2xl font-bold mb-3 font-heading tracking-tight">{t("client_app_title")}</h3>
                                <p className="text-sm text-slate-600 dark:text-slate-400 mb-8 leading-relaxed">{t("client_app_desc")}</p>
                                 <div className="space-y-3">
                                     <Button 
                                        variant="secondary" 
                                        className="w-full h-14 rounded-2xl font-bold gap-3 text-sm group/btn shadow-xl shadow-slate-200 dark:shadow-none hover:scale-[1.02] active:scale-[0.98] transition-all cursor-pointer" 
                                        onClick={() => handleDownload('ios', 'client')}
                                     >
                                         <Apple className="w-5 h-5 fill-current" /> {t("app_store")}
                                     </Button>
                                     <Button 
                                        variant="outline" 
                                        className="w-full h-14 rounded-2xl font-bold gap-3 text-sm border-slate-200 dark:border-white/10 hover:bg-slate-50 dark:hover:bg-white/5 hover:scale-[1.02] active:scale-[0.98] transition-all cursor-pointer" 
                                        onClick={() => handleDownload('android', 'client')}
                                     >
                                         <Download className="w-5 h-5" /> {t("google_play")}
                                     </Button>
                                 </div>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </section>
    );
}